import React from 'react'
import PropTypes from 'prop-types'
import { get, isEqual } from 'lodash'
import { compose, graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { withLoader } from 'evergreen-draft'
import { Provider } from './context'

function identifyUser(user) {
  if (user && window.analytics) {
    window.analytics.identify(user.id, {
      name: user.name,
      email: user.email
    })
  }
}

class UserProvider extends React.Component {
  static propTypes = {
    refreshUser: PropTypes.func,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      isAdmin: PropTypes.bool,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      isAuthorizedViaSSO: PropTypes.bool,
      isAutoprovisionedForSSO: PropTypes.bool
    })
  }

  static defaultProps = {
    refreshUser: () => {}
  }

  static getDerivedStateFromProps({ user, refreshUser }, state) {
    if (isEqual(user, state.value.user)) {
      return null
    }

    return {
      value: {
        user,
        refreshUser
      }
    }
  }

  /**
   * Use `state.value` for context to avoid unnecessary re-renders in consumers
   * @see {@link https://reactjs.org/docs/context.html#caveats}
   */
  state = {
    value: {
      user: this.props.user,
      refreshUser: this.props.refreshUser
    }
  }

  componentDidMount() {
    identifyUser(this.props.user)
  }

  componentDidUpdate(prevProps) {
    if (
      get(prevProps, 'user.id') !== get(this.props, 'user.id') ||
      get(prevProps, 'user.name') !== get(this.props, 'user.name') ||
      get(prevProps, 'user.email') !== get(this.props, 'user.email')
    ) {
      identifyUser(this.props.user)
    }
  }

  render() {
    return <Provider value={this.state.value}>{this.props.children}</Provider>
  }
}

const withUserInfo = graphql(
  gql`
    query getUserInfo {
      me {
        id
        email
        name
        isAdmin
        isAutoprovisionedForSSO
      }
      isAuthorizedViaSSO
    }
  `,
  {
    props: ({ data }) => {
      const user = data.me

      // Only if there's a currently logged in user should we decorate it with this bool
      if (user) {
        user.isAuthorizedViaSSO = Boolean(data.isAuthorizedViaSSO)
      }

      return {
        refreshUser: data.refetch,
        isLoading: data.loading,
        // If there's an error fetching session (which can sometimes be a 401 for invalid token)
        // we still need to be able to let users get to the login page.
        // This ensures we treat fetch errors the same as "no session" by passing a `null` user
        user: data.error ? null : user
      }
    }
  }
)

export default compose(
  withUserInfo,
  withLoader
)(UserProvider)
